import { useState } from 'react';
import { AnimatePresence, motion, type Variants } from 'motion/react';
import type { RevealBottomPanelProps } from '../RevealBottomPanel';
import { FmvOnly } from '../FmvOnly';
import { RevealStage } from '../RevealStage';
import { SleekButton } from '../../theme';
import { sellLabel } from './sellLabel';
import { SoldLine } from './SoldLine';
import { VaultedLine } from './VaultedLine';

// Same choreography as modern's panel: the offer rows drop away bottom-up on
// accept, then the payout and the forward CTA rise in.
const offerContainer: Variants = {
  exit: { transition: { staggerChildren: 0.12, staggerDirection: -1 } },
};
const offerChild: Variants = {
  exit: { y: 60, opacity: 0, transition: { duration: 0.34, ease: [0.4, 0, 1, 1] } },
};
const resolvedContainer: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.16, delayChildren: 0.06 } },
};
const resolvedChild: Variants = {
  hidden: { y: 60, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
};

/**
 * Sleek winner bottom (partner mockup): Fair Market Value under the title,
 * the actions pinned to the bottom of the band — "Vault" beside a white
 * "Sell • N% Value" (the buyback). After a sale: the credited amount and
 * the forward CTA. Vault keeps the card in My Packs without opening the
 * shipping sheet (partner feedback 2026-09-30: it is not a shipping action);
 * nothing is sent to the backend, an opened card is already in the wallet.
 * On the order's last pack the result shows without a CTA and the reveal
 * then leaves by itself (partner Figma 2026-10-15) — see `onResultShown`.
 */
export function SleekRevealBottomPanel({
  item,
  offer,
  sellState,
  onAcceptBuyback,
  onOpenNextPack,
  nextPackCta,
  nextPackIsDone = false,
  onVault,
  onResultShown,
  visibleFmv,
  visibleCta,
  fmvRoll,
  instant,
}: RevealBottomPanelProps) {
  const [isVaulted, setIsVaulted] = useState(false);
  const hasFmv = item.fairMarketValue != null && Number.isFinite(item.fairMarketValue) && item.fairMarketValue > 0;
  const isSelling = sellState === 'selling';
  const isResolved = sellState === 'sold' || isVaulted;

  return (
    <div className="w-full max-w-[480px] mx-auto flex flex-1 flex-col">
      <AnimatePresence mode="wait" initial={false}>
        {!isResolved ? (
          <motion.div key="offer" variants={offerContainer} exit="exit" className="flex flex-1 flex-col items-center justify-between gap-6 w-full">
            {hasFmv && (
              <motion.div variants={offerChild}>
                <RevealStage visible={visibleFmv} instant={instant}>
                  <FmvOnly fmv={item.fairMarketValue!} roll={fmvRoll} instant={instant} />
                </RevealStage>
              </motion.div>
            )}
            <motion.div variants={offerChild} className="w-full">
              <RevealStage visible={visibleCta} instant={instant} className="flex w-full gap-3">
                {/* Always offered — keeping the card isn't a shipping action, so
                    an operator that disables shipping still gets the choice. */}
                <SleekButton name="RevealVaultButton" onClick={() => {
                    setIsVaulted(true);
                    onVault?.();
                  }} disabled={isSelling} className="w-[100px] shrink-0">
                  Vault
                </SleekButton>
                <SleekButton name="RevealSellButton" onClick={onAcceptBuyback} disabled={isSelling} className="flex-1">
                  {isSelling ? 'Selling…' : sellLabel(offer.grossPrice, item.fairMarketValue)}
                </SleekButton>
              </RevealStage>
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            key="resolved"
            variants={resolvedContainer}
            initial="hidden"
            animate="show"
            onAnimationComplete={(definition) => {
              if (definition === 'show') onResultShown?.();
            }}
            className="flex flex-1 flex-col items-center justify-between gap-6 w-full"
          >
            <motion.div variants={resolvedChild}>
              {isVaulted ? <VaultedLine /> : <SoldLine amount={offer.grossPrice} />}
            </motion.div>
            {!onResultShown && (
              <motion.div variants={resolvedChild} className="w-full">
                <SleekButton name={nextPackIsDone ? 'RevealDoneButton' : 'RevealNextPackButton'} onClick={onOpenNextPack} className="w-full">
                  {nextPackCta}
                </SleekButton>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
